<template>
	<Accordion
		:title="title"
		:showClear="showClear"
		:initiallyCollapsed="!automaticallyOpen || isMobile"
		@mouseenter="handleMouseEnter"
		@collapseChange="handleMouseEnter"
		@clear="clearFilter"
	>
		<template #content>
			<component
				v-if="automaticallyOpenedFilterComponent"
				:is="automaticallyOpenedFilterComponent"
			/>

			<ClientOnly v-else>
				<component
					v-if="resolvedFilterComponent"
					:is="resolvedFilterComponent"
				/>

				<div
					v-else-if="isLoadingFilterComponent"
					class="FilterComponentContainer"
					v-skeleton="{
						loading: true,
						sizes: {
							Small: { width: '100%', height: '12rem' },
							ExtraLarge: { width: '100%', height: '12rem' },
						}
					}"
				/>
			</ClientOnly>
		</template>
		<template #footer>
			<a class="Selection">{{ selection }}</a>
		</template>
	</Accordion>
</template>

<script setup lang="ts">
import Accordion from '@components/Accordion/Accordion.vue';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { useSearchStore } from '@app/stores/useSearchStore';
import { useFilterSelectionText } from '@composables/useFilterSelectionText';
import { useMobile } from '@composables/useMobile';
import { useMobileModal } from '@composables/useMobileModal';
import { computed, defineAsyncComponent, onMounted, shallowRef, ref, type Component, PropType, type Ref, watch } from 'vue';
import { FilterProcessingService } from '@app/routing/FilterProcessingService';
import { PortalType } from '@studyportals/domain-client';

type FilterComponentLoader = () => Promise<{ default: Component }>;

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	filterTypes: {
		type: Array as PropType<FilterType[]>,
		required: true,
	},
	filterComponentLoader: {
		type: Function as PropType<FilterComponentLoader | null>,
		default: null,
	},
	automaticallyOpen: {
		type: Boolean,
		default: false,
	},
});

const searchStore = useSearchStore();
const { isMobile } = useMobile();
const { modalState } = useMobileModal();
const filtersProcessingService = FilterProcessingService.getInstance();
const selection = useFilterSelectionText(props.filterTypes);
const automaticallyOpenedFilterComponent = props.automaticallyOpen && props.filterComponentLoader
	? defineAsyncComponent(props.filterComponentLoader)
	: null;
const resolvedFilterComponent = shallowRef<Component | null>(null);
const isLoadingFilterComponent = ref(false);
let loadPromise: Promise<void> | null = null;
const loadFilterComponent = (): void => {
	if (!props.filterComponentLoader || resolvedFilterComponent.value || loadPromise) return;

	isLoadingFilterComponent.value = true;
	loadPromise = props.filterComponentLoader()
		.then((module) => {
			resolvedFilterComponent.value = module.default;
		})
		.finally(() => {
			isLoadingFilterComponent.value = false;
			loadPromise = null;
		});
};

onMounted(() => {
	if (props.automaticallyOpen || !isMobile.value || !modalState.value.isOpen) return;

	loadFilterComponent();
});

const handleMouseEnter = (): void => {
	if (isMobile.value) return;

	loadFilterComponent();
};

const showClear = computed(() => {
	const hasNonImplicit = props.filterTypes.some((filterType) => {
		const filter = searchStore.filtersSelection.find((f) => f.type === filterType);
		if (!filter) return false;

		const mapper = filtersProcessingService.mappers.find((m) => m.filterType === filterType);
		if (!mapper) return filter.values.length > 0;

		return filter.values.some(
			(v) => !mapper.isImplicitForPortalType(v.id, searchStore.portalType as PortalType)
		);
	});
	return hasNonImplicit;
});

const clearFilter = (): void => {
	searchStore.removeFilterByType(props.filterTypes);
};

watch(
	() => isMobile.value,
	(newValue) => {
		if (!newValue) return;

		loadFilterComponent();
	}
);
</script>

<style scoped lang="scss">
	.FilterComponentContainer {
		display: block;
		width: 100%;
	}

	.Selection {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		word-break: break-word;
		color: $Grey-700;
		user-select: none;

		@include TextStyle(BodyMediumRegular);
	}
</style>
